import { useParams } from "react-router-dom";
import React, { useCallback, useState, useEffect } from "react";
import { trackPromise } from "react-promise-tracker";
import {
  Box,
  Fade,
  FormControl,
  InputLabel,
  Modal,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  ResumoOportunidadebuscarProdutoOportunidade,
  detalheResumoOportunidade,
  listarCdsResumoOportunidadeSeller,
} from "../../../services/http/oportunidadeService";
import { ReactComponent as CircleLeft } from "../../../assets/icons/circle-left.svg";
import { ReactComponent as CircleRight } from "../../../assets/icons/circle-right.svg";
import { convertTextUnitToNumber } from "../../../util/NumberConvert";
import { useSnackBar } from "../../../hooks/snackBar";
import "./index.scss";

function ModalOportunidade({ showModal, handleCloseModal, data }) {
  const [gotProductDetails, setGotProductDetails] = useState(true);
  const { id } = useParams();
  const { addSnackBar } = useSnackBar();
  const [position, setPosition] = useState(0);
  const [values, setValues] = useState({
    dadosEmpresaSellerId: 0,
    codigo: "",
    detalheLote: [
      {
        urlImagens: "",
        gtinEan: "",
        descricao: "",
        marca: "",
        dataValidade: "",
        tipoFrete: "",
        ncm: "",
        gtinDun14Caixa: "",
        motivoOferta: "",
        precoCaixa: "",
        quantidadePorCaixa: "",
        estoqueDisponivelCaixas: "",
        precoVenda_NF_PorCaixa: "",
        pedidoMinimo: "",
        precoUnidade: "",
        feeGreenersPrecoMedioVarejo: "",
        valorEconomia: "",
        markup: "",

        localizacaoCarga: [
          {
            cidade: "",
            estado: "",
            quantidade: "",
          },
        ],
      },
    ],
  });

  const nextIsValid = useCallback(
    () => position < values?.detalheLote.length - 1,
    [position, values?.detalheLote.length]
  );

  const previousIsValid = useCallback(() => position > 0, [position]);

  const handleGoToNextProduct = useCallback(() => {
    if (nextIsValid()) {
      setPosition((old) => old + 1);
      buscarOportunidade(data?.id);
    }
  }, [nextIsValid]);

  const handleGoToPreviousProduct = useCallback(() => {
    if (previousIsValid()) {
      setPosition((old) => old - 1);
      buscarOportunidade(data?.id);
    }
  }, [previousIsValid]);

  const handleConvertToCurrencyText = useCallback((value) => {
    const numberWithDecimals = Number(value || 0).toFixed(2);
    return Number(numberWithDecimals).toLocaleString("pt-br", {
      style: "currency",
      currency: "BRL",
    });
  }, []);

  const buscarOportunidade = (oportunidadeId) => {
    trackPromise(
      detalheResumoOportunidade(oportunidadeId).then(async (res) => {
        if (res.fail) {
          throw new Error(res.errors);
        }

        const _values = {
          ...res.data,
          detalheLote: res.data?.detalheLote,
        };

        setValues(_values);
        setGotProductDetails(false);
      })
    ).catch((error) => {
      addSnackBar({
        message: "Não foi possível encontrar a oportunidade.",
        info: "error",
        open: true,
      });
      console.log(error);
    });
  };

  const buscarDetalheProdutos = async () => {
    const product_promises = [];
    const _values = { ...values };

    const { data: locations } = await listarCdsResumoOportunidadeSeller(
      values.dadosEmpresaSellerId
    );

    _values.detalheLote.forEach((detalheLote, index) => {
      if (detalheLote.gtinEan) {
        product_promises.push(
          ResumoOportunidadebuscarProdutoOportunidade(
            values.dadosEmpresaSellerId,
            detalheLote.gtinEan
          )
        );
      }
    });

    Promise.all(product_promises)
      .then((list) => {
        list.forEach(({ data }) => {
          const index = _values.detalheLote.findIndex(
            (item) => item.gtinEan === data.gtin
          );

          if (index < 0)
            return

          const precoUnidade =
            _values.detalheLote[index].precoVenda_NF_PorCaixa /
              convertTextUnitToNumber(data.quantidadePorCaixa) || 0;

          const economy = !!_values.detalheLote[index]
            .feeGreenersPrecoMedioVarejo
            ? Math.max(
                0,
                Number(
                  _values.detalheLote[index].feeGreenersPrecoMedioVarejo
                )
              ) -
              _values.detalheLote[index].precoVenda_NF_PorCaixa /
                convertTextUnitToNumber(data.quantidadePorCaixa)
            : 0;

          _values.detalheLote[index]?.localizacaoCarga?.map((location) => {
            const locationDetails = locations.find(
              (item) => item.id === location.id
            );
            if (locationDetails) {
              return {
                ...location,
                cidade: locationDetails.cidade,
                estado: locationDetails.estado,
              };
            }
            return location;
          });

          _values.detalheLote[index] = {
            ..._values.detalheLote[index],
            ...data,
            precoUnidade: precoUnidade,
            valorEconomia: economy,
            feeGreenersPrecoMedioVarejo: Number(
              _values.detalheLote[index].feeGreenersPrecoMedioVarejo
            ),
          };
        });
      })
      .finally(() => {
        setValues(_values);
        setGotProductDetails(true);
      });
  };

  useEffect(() => {
    setValues({
      ...data,
      detalheLote: data?.detalheLote ? data?.detalheLote : [],
    });
  }, [data]);

  useEffect(() => {
    if (id) {
      buscarOportunidade(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);  

  useEffect(() => {
    if (!gotProductDetails) {
      buscarDetalheProdutos();
    }
  }, [gotProductDetails]);  

  return (
    <Modal className="modal-size" open={showModal} close={handleCloseModal}>
      <div className={"modal-content defaultFont"}>
        <Fade
          style={{ transitionDuration: 1000 }}
          in={true}
          key={values.codigo}
        >
          <Box sx={{ width: "100%" }}>
            <div className="row mb20">
              <div className="col-sm-10 col-md-10 col-lg-10">
                <h1 className="titulo" style={{ margin: 0 }}>
                  Resumo da oportunidade
                </h1>
                <Typography variant="caption" style={{ opacity: 0.5 }}>
                  ID da Oportunidade: #
                  {values?.codigo
                    ? values?.codigo?.toString().padStart(6, "0")
                    : "000000"}
                </Typography>
              </div>
              <div className="col-sm-2 col-md-2 col-lg-2 modal-header">
                <button
                  type="button"
                  title="Clique para visualizar a oportunidade anterior."
                  onClick={handleGoToPreviousProduct}
                  disabled={!previousIsValid()}
                  style={{ opacity: !previousIsValid() ? 0.3 : 1 }}
                >
                  <CircleLeft />
                </button>
                <button
                  type="button"
                  title="Clique para visualizar a próxima oportunidade."
                  onClick={handleGoToNextProduct}
                  disabled={!nextIsValid()}
                  style={{ opacity: !nextIsValid() ? 0.3 : 1 }}
                >
                  <span className=" material-icons_outlined">
                    <CircleRight />
                  </span>
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-3 col-md-3 col-lg-3 summary-image-container">
                <img
                  alt={values?.detalheLote[position]?.descricao}
                  src={values?.detalheLote[position]?.urlImagens}
                  className="summary-product-image"
                />
              </div>
              <div className="col-sm-9 col-md-9 col-lg-9">
                <div className="row">
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="EAN"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.gtinEan}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-8 col-md-8 col-lg-8">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Descrição Curta"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.descricao}
                      />
                    </FormControl>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Marca"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.marca}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Validade"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled
                        value={values?.detalheLote[position]?.dataValidade}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Tipo de Frete"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.tipoFrete}
                      />
                    </FormControl>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="NCM"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.ncm || ""}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="GTIN/DUN"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.gtinDun14Caixa}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Motivo da Oferta"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={values?.detalheLote[position]?.motivoOferta}
                      />
                    </FormControl>
                  </div>
                </div>

                {values?.detalheLote[position]?.localizacaoCarga &&
                  values?.detalheLote[position]?.localizacaoCarga.map(
                    (local, index) => (
                      <div className="row" key={local.unidade + index}>
                        <div className="col-sm-4 col-md-4 col-lg-4">
                          <FormControl>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Localização da Carga (Cidade)"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              disabled
                              value={local.unidade?.dadosCadastrais?.endereco?.cidade}
                            />
                          </FormControl>
                        </div>
                        <div className="col-sm-4 col-md-4 col-lg-4">
                          <FormControl>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Localização da Carga (UF)"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              disabled
                              value={local.unidade?.dadosCadastrais?.endereco?.estado}
                            />
                          </FormControl>
                        </div>
                        <div className="col-sm-3 col-md-3 col-lg-3">
                          <FormControl>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Quantidade (caixas)"
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              disabled
                              value={local.quantidade}
                            />
                          </FormControl>
                        </div>
                      </div>
                    )
                  )}

                <div className="row">
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Preço por Caixa"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        className="summary-field-green"
                        value={handleConvertToCurrencyText(
                          values?.detalheLote[position]?.precoCaixa
                        )}
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Quantidade por Caixa"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={
                          values?.detalheLote[position]?.quantidadePorCaixa
                        }
                      />
                    </FormControl>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Estoque Disponível (Caixas)"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        disabled
                        value={
                          values?.detalheLote[position]?.estoqueDisponivelCaixas
                        }
                      />
                    </FormControl>
                  </div>
                  <div className="col-sm-4 col-md-4 col-lg-4">
                    <FormControl>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Pedido Mínimo (Caixas)"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled
                        value={values?.detalheLote[position]?.pedidoMinimo}
                      />
                    </FormControl>
                  </div>
                </div>

<div className="row">
  <div className="col-sm-4 col-md-4 col-lg-4">
    <FormControl>
      <TextField
        fullWidth
        variant="outlined"
        label="Preço Unitário"
        size="small"
        className="summary-field-green"
        disabled
        value={handleConvertToCurrencyText(
          values?.detalheLoteDto[position]
            ?.precoUnitarioFee
        )}
      />
    </FormControl>
  </div>
  <div className="col-sm-4 col-md-4 col-lg-4">
    <FormControl>
      <TextField
        fullWidth
        variant="outlined"
        label="Preço Médio Varejo (Unidade)"
        size="small"
        disabled
        value={handleConvertToCurrencyText(
          values?.detalheLoteDto[position]?.feeGreenersPrecoMedioVarejo
        )}
      />
    </FormControl>
  </div>
</div>

<div className="row">
  <div className="col-sm-5 col-md-5 col-lg-5">
    <FormControl>
      <TextField
        fullWidth
        variant="outlined"
        label="Diferença – Preço de varejo x Preço unitário"
        size="small"
        disabled
        value={handleConvertToCurrencyText(
          values?.detalheLoteDto[position]?.valorEconomia
        )}
      />
    </FormControl>
  </div>

  <div className="col-sm-4 col-md-4 col-lg-4">
    <FormControl>
      <TextField
        fullWidth
        variant="outlined"
        label="Markup – preço unitário x preço varejo"
        size="small"
        disabled
        value={`${
          values?.detalheLoteDto[position]?.markup || 0
        }%`}
      />
    </FormControl>
  </div>
</div>
              </div>
            </div>

            <div className="row" style={{ justifyContent: "center" }}>
              <button
                className="mt20 buton-white-black-border wMax400"
                onClick={handleCloseModal}
              >
                FECHAR
              </button>
            </div>
          </Box>
        </Fade>
      </div>
    </Modal>
  );
}

export default ModalOportunidade;
